import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { DataLoader } from '@/components/ui/DataLoader';
import { useFundsPageData } from '@/hooks/react-query/useFunds';
import { useAllFundsCommitmentTotals } from '@/hooks/react-query/useInvestors';
import { FundsList } from '@/components/funds/FundsList';
import { FundsSummary } from '@/components/funds/FundsSummary';
import { FundsHeader } from '@/components/funds/FundsHeader';
import { FundsEmptyState } from '@/components/funds/FundsEmptyState';

export default function FundsPage() {
  const navigate = useNavigate();
  const { funds, statistics, isLoading, error } = useFundsPageData();
  const { data: commitmentTotals = {} } = useAllFundsCommitmentTotals();
  const [expandedFunds, setExpandedFunds] = useState<Set<string>>(
    () => new Set()
  );

  // Get main funds (no parent)
  const mainFunds = useMemo(
    () => funds.filter((fund) => !fund.parent_fund_id),
    [funds]
  );

  const handleExpandAll = useCallback(() => {
    const allMainFundIds = mainFunds
      .filter((fund) => funds.some((f) => f.parent_fund_id === fund.id))
      .map((fund) => fund.id);
    setExpandedFunds(new Set(allMainFundIds));
  }, [mainFunds, funds]);

  const handleCollapseAll = useCallback(() => {
    setExpandedFunds(new Set());
  }, []);

  const toggleFundExpansion = (fundId: string) => {
    setExpandedFunds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fundId)) {
        newSet.delete(fundId);
      } else {
        newSet.add(fundId);
      }
      return newSet;
    });
  };

  return (
    <DataLoader
      data={funds}
      isLoading={isLoading}
      error={error}
      emptyStateMessage="No funds created yet"
      showEmptyState={false}
    >
      {() => (
        <div className="p-6 space-y-6">
          {/* Funds Header */}
          <FundsHeader
            funds={funds}
            expandedFunds={expandedFunds}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
          />

          {/* Summary Stats */}
          <FundsSummary statistics={statistics} />

          {/* Fund List */}
          {mainFunds.length > 0 && (
            <FundsList
              funds={funds}
              expandedFunds={expandedFunds}
              onToggleFundExpansion={toggleFundExpansion}
              commitmentTotals={commitmentTotals}
            />
          )}

          {/* Empty State - only show when no data at all */}
          {mainFunds.length === 0 && (
            <FundsEmptyState onCreateFund={() => navigate('/app/funds/new')} />
          )}
        </div>
      )}
    </DataLoader>
  );
}
